import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useSendModerationNotification } from '@/features/moderation/api';
import { systemNotificationSchema, type SystemNotificationForm } from '../schemas';

const DEFAULT_TITLE = 'System Notification';

/**
 * Send System Notification dialog (port of SystemNotificationDialog.kt).
 * Posts to POST /admin/api/moderation/send-notification (same path as Moderation Task B).
 * Send stays disabled until the note is non-blank.
 */
export function SystemNotificationDialog({
  open,
  onOpenChange,
  userId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Target application user id. Dialog cannot send without a valid id. */
  userId?: string | number | null | undefined;
}) {
  const sendMut = useSendModerationNotification();
  const form = useForm<SystemNotificationForm>({
    resolver: zodResolver(systemNotificationSchema),
    defaultValues: { note: '' },
    mode: 'onChange',
  });

  useEffect(() => {
    if (!open) form.reset();
  }, [open, form]);

  const numericUserId = userId != null && String(userId).trim() !== '' ? Number(userId) : NaN;
  const canSend = Number.isFinite(numericUserId) && numericUserId > 0;

  const onSubmit = form.handleSubmit(async (values) => {
    if (!canSend) {
      toast.error('No user to notify.');
      return;
    }
    try {
      await sendMut.mutateAsync({
        userId: numericUserId,
        title: DEFAULT_TITLE,
        message: values.note,
      });
      toast.success('Notification sent.');
      form.reset();
      onOpenChange(false);
    } catch {
      toast.error('Failed to send notification.');
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) form.reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Send System Notification</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-3">
          <Textarea rows={5} placeholder="Write a notification…" {...form.register('note')} />
          {form.formState.errors.note && (
            <p className="text-xs text-destructive">{form.formState.errors.note.message}</p>
          )}
          {!canSend ? (
            <p className="text-xs text-destructive">Missing user id — cannot send.</p>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={sendMut.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!form.formState.isValid || !canSend || sendMut.isPending}
            >
              {sendMut.isPending ? 'Sending…' : 'Send'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
